import * as Sentry from '@sentry/react';
import type { TenantInfo } from '../services/hotel.service';

const PII_KEYS = ['sessionToken', 'email', 'password', 'authorization', 'cookie', 'token'];

function scrubPII<T>(value: T): T {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(scrubPII) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (PII_KEYS.some((p) => k.toLowerCase().includes(p))) {
      out[k] = '[REDACTED]';
    } else if (v && typeof v === 'object') {
      out[k] = scrubPII(v);
    } else {
      out[k] = v;
    }
  }
  return out as unknown as T;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[Sentry] VITE_SENTRY_DSN not set — error reporting disabled.');
    }
    return;
  }

  const release =
    (window as unknown as { __APP_BUILD_ID__?: string }).__APP_BUILD_ID__ || 'unknown';

  Sentry.init({
    dsn,
    release,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'ChunkLoadError',
      'Loading chunk',
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'AbortError',
      'NetworkError when attempting to fetch resource',
      'cancelled',
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i, /^safari-extension:\/\//i],
    beforeSend(event) {
      // Drop React's dev-only `Warning:` lines — they're hints, not bugs,
      // and would dominate the dashboard. Production builds don't emit them.
      const msg = event.message || event.exception?.values?.[0]?.value || '';
      if (typeof msg === 'string' && msg.startsWith('Warning:') && import.meta.env.DEV) {
        return null;
      }
      if (event.request?.cookies) delete event.request.cookies;
      if (event.request?.headers) {
        event.request.headers = scrubPII(event.request.headers);
      }
      if (event.extra) event.extra = scrubPII(event.extra);
      if (event.contexts) event.contexts = scrubPII(event.contexts);
      return event;
    },
  });
}

export function setSentryTenant(tenant: TenantInfo | null): void {
  if (!tenant) {
    Sentry.setTag('hotelId', undefined);
    return;
  }
  Sentry.setTag('hotelId', tenant.id);
  Sentry.setTag('tenantSlug', (tenant as unknown as { slug?: string }).slug || tenant.id);
}

export function setSentryUser(params: { id?: string; email?: string } | null): void {
  if (!params || (!params.id && !params.email)) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: params.id || params.email,
    email: params.email,
  });
}

export { Sentry };
